import React, { useEffect, useState } from 'react';
import { Button, message } from 'antd';
import { resumeManagedBilling } from 'api/insuranceBilling';
import type { ManagedBillingScopeReq, ManagedBillingVo } from 'types/insuranceBilling';
import ConfirmModal from './ConfirmModal';
import EhrTiles from './EhrTiles';
import ProviderPicker from './ProviderPicker';
import s from './s.module.less';

type Step = 'prov' | 'ehr';

type Props = {
    open: boolean;
    state: ManagedBillingVo;
    isGroup: boolean;
    selfProviderId?: number;
    onClose: () => void;
    onDone: (next: ManagedBillingVo) => void;
};

const RESUME_COPY =
    'Klarity starts pulling visits and creating claims again from today. Visits from while it was off are not billed automatically; contact us if you need those reviewed.';

// 原型 confirmClaimOn：整个账号关掉后重新打开，默认全选（取消勾选的继续保持关闭）。
// group owner：providers → EHRs；solo：EHRs
export default function TurnOnModal({ open, state, isGroup, selfProviderId, onClose, onDone }: Props) {
    const group = isGroup && state.providers.length > 0;
    const allEhrs = state.ehrs.map((e) => e.ehr);
    // 一个 EHR 都没连：没得勾，直接打开整个账号（group 省掉 EHR 步，solo 只剩说明 + Turn on）
    const noEhr = !allEhrs.length;
    const steps: Step[] = group && !noEhr ? ['prov', 'ehr'] : [group ? 'prov' : 'ehr'];
    const allProvIds = state.providers.map((p) => p.id);
    const [at, setAt] = useState(0);
    const [ehrs, setEhrs] = useState<string[]>(allEhrs);
    const [provIds, setProvIds] = useState<number[]>(allProvIds);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setAt(0);
        setEhrs(state.ehrs.map((e) => e.ehr));
        setProvIds(state.providers.map((p) => p.id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const step = steps[at];
    const pickedEhrs = allEhrs.filter((n) => ehrs.includes(n));
    const ehrValid = noEhr || pickedEhrs.length > 0;
    const stepValid = step === 'prov' ? provIds.length > 0 : ehrValid;
    const last = at === steps.length - 1;

    const handleSubmit = async () => {
        if (!ehrValid || (group && !provIds.length)) return;
        const everyEhr = pickedEhrs.length === allEhrs.length;
        const everyone = !group || provIds.length === allProvIds.length;
        // 全部勾选 = 整个账号恢复（claim start 也移到今天）；否则只恢复勾选的范围，其余保持关闭
        const req: ManagedBillingScopeReq = {
            scope: everyEhr ? 'all' : 'ehrs',
            ehrs: everyEhr ? undefined : pickedEhrs,
            providerIds: everyone ? undefined : provIds,
        };
        setSubmitting(true);
        const res: any = await resumeManagedBilling(req);
        setSubmitting(false);
        if (res?.error) return;
        message.success('Managed billing turned on');
        onDone(res?.data?.data);
    };

    const intro = at === 0 ? <p className={s.p}>{RESUME_COPY}</p> : null;
    const body =
        step === 'prov' ? (
            <>
                {intro}
                <div className={s.label}>Turn billing back on for</div>
                <ProviderPicker
                    providers={state.providers}
                    value={provIds}
                    onChange={setProvIds}
                    selfProviderId={selfProviderId}
                />
            </>
        ) : (
            <>
                {intro}
                {!noEhr && (
                    <>
                        <div className={s.label}>{group ? 'In which EHRs' : 'Turn billing back on for'}</div>
                        {group && <p className={s.who}>For the providers you selected</p>}
                        <EhrTiles names={allEhrs} value={ehrs} onChange={setEhrs} />
                    </>
                )}
            </>
        );

    return (
        <ConfirmModal
            open={open}
            title="Turn managed billing back on?"
            onClose={onClose}
            footer={
                <>
                    {at === 0 ? (
                        <Button onClick={onClose}>Cancel</Button>
                    ) : (
                        <Button onClick={() => setAt(at - 1)}>Back</Button>
                    )}
                    {last ? (
                        <Button type="primary" disabled={!stepValid} loading={submitting} onClick={handleSubmit}>
                            Turn on
                        </Button>
                    ) : (
                        <Button type="primary" disabled={!stepValid} onClick={() => setAt(at + 1)}>
                            Continue
                        </Button>
                    )}
                </>
            }
        >
            {steps.length > 1 && (
                <div className={s.stepNo}>
                    Step {at + 1} of {steps.length}
                </div>
            )}
            {body}
        </ConfirmModal>
    );
}
